import { ReactNode, useState } from "react"

import {
  Box,
  Button,
  LinearProgress,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material"
import { useTranslation } from "react-i18next"
import { UseQueryResult } from "react-query"

export type SectionState = "loading" | "error" | "empty" | "updating" | "ready"

export const useSectionState = (
  queries: UseQueryResult<unknown>[],
  isEmpty: boolean,
): { state: SectionState; retry: () => void } => {
  const retry = () => queries.forEach((query) => query.refetch())

  // First load only: with keepPreviousData, a period change keeps the old data.
  if (queries.some((query) => query.isLoading))
    return { state: "loading", retry }
  // A failed background refetch keeps the last good data (RQ v3 keeps `data`
  // on error), so the error state only shows when there is nothing to show.
  if (queries.some((query) => query.isError && query.data === undefined))
    return { state: "error", retry }
  if (isEmpty) return { state: "empty", retry }
  if (queries.some((query) => query.isFetching && query.isPreviousData))
    return { state: "updating", retry }
  return { state: "ready", retry }
}

// With keepPreviousData the data on screen can still belong to the previous
// query key; returns the input that key was built from, so labels, ranks and
// colours describe the rows actually shown.
export const useShownValue = <T,>(value: T, isPreviousData: boolean): T => {
  const [shown, setShown] = useState(value)
  if (!isPreviousData && !Object.is(shown, value)) setShown(value)
  return isPreviousData ? shown : value
}

// Rows shaped like RankedBarRow, so the card keeps its height while loading.
export const RowsSkeleton = ({ rows }: { rows: number }) => (
  <Stack rowGap={1.25} aria-busy="true" data-testid="section-skeleton">
    {Array.from({ length: rows }, (_, index) => (
      <Skeleton key={index} variant="rounded" height={22} />
    ))}
  </Stack>
)

const DashboardSectionBody = ({
  queries,
  isEmpty = false,
  emptyText,
  skeleton,
  progressTop = -12,
  children,
}: {
  queries: UseQueryResult<unknown>[]
  isEmpty?: boolean
  // Plain copy, or a node of its own (e.g. a search's no-matches message)
  emptyText?: ReactNode
  skeleton: ReactNode
  progressTop?: number
  children: ReactNode
}) => {
  const { t } = useTranslation()
  const { state, retry } = useSectionState(queries, isEmpty)

  if (state === "loading") return <>{skeleton}</>

  if (state === "error")
    return (
      <Stack alignItems="center" rowGap={1} sx={{ py: 4, textAlign: "center" }}>
        <Typography variant="body2" color="mockup.neutral30">
          {t("adminDashboard.sectionError")}
        </Typography>
        <Button variant="outlined" size="small" onClick={retry}>
          {t("adminDashboard.tryAgain")}
        </Button>
      </Stack>
    )

  if (state === "empty" && typeof emptyText !== "string")
    return <>{emptyText}</>

  if (state === "empty")
    return (
      <Typography
        variant="body2"
        color="mockup.neutral30"
        sx={{ py: 4, textAlign: "center" }}
      >
        {emptyText}
      </Typography>
    )

  return (
    <Box sx={{ position: "relative" }}>
      {state === "updating" && (
        <LinearProgress
          aria-label={t("adminDashboard.updating")}
          sx={{
            position: "absolute",
            top: progressTop,
            left: 0,
            right: 0,
            height: 2,
          }}
        />
      )}
      <Box
        sx={{
          opacity: state === "updating" ? 0.6 : 1,
          transition: "opacity 150ms",
        }}
      >
        {children}
      </Box>
    </Box>
  )
}

export default DashboardSectionBody
